/**
 * Audio service for microphone recording
 * Uses Web Audio API to capture raw PCM audio for AWS Transcribe
 */

import { websocketService } from './websocketService';

// ===== TYPES =====

export interface AudioConfig {
  sampleRate?: number;
  channelCount?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
}

// ===== SERVICE =====

class AudioService {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private isRecording = false;
  private isPaused = false;
  private sampleRate: number = 16000; // browser sample rate after AudioContext creation

  /**
   * Request microphone permission and get audio stream
   * Also creates AudioContext to determine the actual sample rate
   */
  async requestMicrophoneAccess(config: AudioConfig = {}): Promise<MediaStream> {
    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          sampleRate: config.sampleRate || 16000,
          channelCount: config.channelCount || 1,
          echoCancellation: config.echoCancellation ?? true,
          noiseSuppression: config.noiseSuppression ?? true,
          autoGainControl: config.autoGainControl ?? true,
        },
        video: false,
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Create AudioContext immediately to get the actual browser sample rate
      // This ensures getSampleRate() returns the correct value before startRecording()
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
        this.sampleRate = this.audioContext.sampleRate;
        console.log('AudioContext created with sample rate:', this.sampleRate);
      }
      
      return this.mediaStream;
    } catch (error: any) {
      console.error('Failed to access microphone:', error);
      throw new Error(
        error.name === 'NotAllowedError'
          ? 'Microphone access denied. Please allow microphone access in your browser settings.'
          : 'Failed to access microphone: ' + error.message
      );
    }
  }

  /**
   * Start recording audio using Web Audio API for raw PCM capture
   */
  async startRecording(stream?: MediaStream): Promise<void> {
    if (this.isRecording) {
      console.warn('Recording already in progress');
      return;
    }

    const audioStream = stream || this.mediaStream;
    if (!audioStream) {
      throw new Error('No audio stream available. Call requestMicrophoneAccess first.');
    }

    try {
      // AudioContext should already exist from requestMicrophoneAccess()
      // But create it here if it doesn't (fallback for direct startRecording calls)
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
        this.sampleRate = this.audioContext.sampleRate;
        console.log('AudioContext created with sample rate:', this.sampleRate);
      }
      
      // Create source from microphone stream
      this.source = this.audioContext.createMediaStreamSource(audioStream);
      
      // Create ScriptProcessor for raw audio capture
      // Buffer size: 4096 samples, 1 input channel, 1 output channel
      this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
      
      // Process audio samples
      this.scriptProcessor.onaudioprocess = (event) => {
        if (this.isPaused) {
          // Don't send audio when paused
          return;
        }
        
        // Get audio data (Float32Array with values between -1.0 and 1.0)
        const float32Data = event.inputBuffer.getChannelData(0);
        
        // Convert to Int16 PCM (AWS Transcribe requirement)
        const int16Data = this.float32ToInt16(float32Data);
        
        // Send raw PCM bytes via WebSocket
        if (websocketService.isConnected()) {
          websocketService.sendAudioData(int16Data.buffer as ArrayBuffer);
        }
      };
      
      // Connect audio graph
      this.source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
      
      this.isRecording = true;
      this.isPaused = false;
      
    } catch (error: any) {
      console.error('Failed to start recording:', error);
      throw new Error('Failed to start recording: ' + error.message);
    }
  }

  /**
   * Convert Float32 audio samples to Int16 PCM
   */
  private float32ToInt16(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Clamp to [-1, 1] range
      const sample = Math.max(-1, Math.min(1, float32Array[i]));
      // Convert to Int16 range: [-32768, 32767]
      int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    return int16Array;
  }

  /**
   * Pause recording
   */
  pauseRecording(): void {
    if (this.isRecording && !this.isPaused) {
      this.isPaused = true;
      console.log('Recording paused');
    }
  }

  /**
   * Resume recording
   */
  resumeRecording(): void {
    if (this.isRecording && this.isPaused) {
      this.isPaused = false;
      console.log('Recording resumed');
    }
  }

  /**
   * Stop recording
   */
  async stopRecording(): Promise<Blob> {
    if (!this.isRecording) {
      throw new Error('No active recording');
    }

    try {
      // Disconnect audio graph
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      
      if (this.source) {
        this.source.disconnect();
        this.source = null;
      }
      
      // Close audio context
      if (this.audioContext) {
        await this.audioContext.close();
        this.audioContext = null;
      }
      
      this.isRecording = false;
      this.isPaused = false;
      console.log('Recording stopped');
      
      // Return empty blob (we're streaming, not collecting)
      return new Blob([], { type: 'audio/pcm' });
      
    } catch (error: any) {
      console.error('Failed to stop recording:', error);
      throw error;
    }
  }

  /**
   * Release microphone and clean up resources
   */
  releaseMicrophone(): void {
    // Stop all tracks in the media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }

    // Clean up audio context
    if (this.audioContext) {
      this.audioContext.close().catch(console.error);
      this.audioContext = null;
    }

    // Clean up script processor
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    // Clean up source
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    this.isRecording = false;
    this.isPaused = false;
    console.log('Microphone released');
  }

  /**
   * Get the audio stream
   */
  getMediaStream(): MediaStream | null {
    return this.mediaStream;
  }

  /**
   * Check if currently recording
   */
  getIsRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Get the current audio format being used
   */
  getAudioFormat(): string {
    return 'audio/pcm';  // Always PCM now
  }

  /**
   * Get the sample rate being used by the AudioContext
   */
  getSampleRate(): number {
    return this.sampleRate;
  }

  /**
   * Check if browser supports Web Audio API
   */
  static isSupported(): boolean {
    return !!(
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof AudioContext !== 'undefined'
    );
  }

  /**
   * Get audio level from stream (for visualizer)
   */
  getAudioLevel(stream?: MediaStream): number {
    const audioStream = stream || this.mediaStream;
    if (!audioStream) return 0;

    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(audioStream);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      microphone.connect(analyser);
      analyser.getByteTimeDomainData(dataArray);

      // Calculate RMS (Root Mean Square) for audio level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      return rms;
    } catch (error) {
      console.error('Failed to get audio level:', error);
      return 0;
    }
  }
}

// Export singleton instance
export const audioService = new AudioService();
